/**
 * Save-time SMS validation for the Workflow2 builder.
 *
 * A workflow SMS action stores its message(s) in `node.properties.smsRules[].smsText` — TEMPLATES
 * with {{merge_tags}} that are substituted with real patient data at SEND time. Unlike a chat/SMS
 * composer there is no live "send" here, so this is the author-time gate: measure each rule's
 * RENDERED size (conservative sample values, exactly like the campaign composer) via the shared
 * `getSmsGuidance` and surface anything that would be carrier-blocked (> the 10-segment cap →
 * Twilio 30019, billed but never delivered) or is near the cap / cost-inflated by a special char.
 *
 * Pure + injectable (`getGuidance`) so it unit-tests without the Vue component. The heavy
 * segment/encoding math lives in getSmsGuidance → smsSegments (byte-for-byte with the Lambda layer).
 *
 * KEEP IN SYNC WITH: cypress/node-tests/workflow-sms-validation.test.js
 */
import { getSmsGuidance } from '../../../utils/smsGuidance';

/**
 * @param {Array} nodes            WorkflowBuilder nodes (this.nodes)
 * @param {Function} [getGuidance] injectable smsGuidance (defaults to the shared getSmsGuidance)
 * @returns {{ blocks: Array, warnings: Array }} each entry: { nodeId, ruleIndex, segments, message, reason? }
 *   blocks   = over the carrier cap even with sample data → the save must be prevented.
 *   warnings = near-cap headroom / special-char / long → informational, save allowed.
 */
export function collectWorkflowSmsIssues(nodes, getGuidance) {
  const guide = typeof getGuidance === 'function' ? getGuidance : getSmsGuidance;
  const blocks = [];
  const warnings = [];

  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (!node || node.type !== 'sms') return;
    const rules = (node.properties && Array.isArray(node.properties.smsRules)) ? node.properties.smsRules : [];
    rules.forEach((rule, ruleIndex) => {
      const text = rule && rule.smsText;
      if (text == null || String(text).trim() === '') return; // empty/not-yet-authored rule → nothing to measure
      // substituteTags: this is a template — measure the rendered text, not the literal {{tags}}.
      const g = guide(text, { substituteTags: true }) || {};
      const entry = { nodeId: node.id, ruleIndex, segments: g.segments, message: g.message, reason: g.reason };
      if (g.severity === 'block') blocks.push(entry);
      else if (g.severity === 'warn') warnings.push(entry);
    });
  });

  return { blocks, warnings };
}
